import { useImage } from '@/composables/UseImage';
import { useConfiguratorStore } from '@/stores/Configurator/ConfiguratorStore';
import type { ImageDimensions } from '@/types/Configurator/ImageDimensions';
import { SelectorOption } from '@/types/Configurator/SelectorOption';
import { FilterRecord } from '@/types/Filters/FilterRecord';
import { FilterValueRecord } from '@/types/Filters/FilterValueRecord';
import {
  ConfigurableProductFilterObject,
  ConfigurableProductFilterValueObject,
  ConfigurableProductSizeValueObject,
} from '@/types/joostconnector';
import _ from 'lodash';
import { defineStore } from 'pinia';
import { computed, ComputedRef, ref, Ref } from 'vue';

export const cutoutKey = 'uitsnede';
export const amountHexagonsKey = 'aantalHexagon';

export const useFilterStore = defineStore(
  `filter-${window.product.sku}`,
  () => {
    /**
     * Map filter value object to SelectorOption
     * @param value
     * @param filter
     */
    function filterValueToDropdown(
      value: ConfigurableProductFilterValueObject,
      filter?: ConfigurableProductFilterObject
    ): SelectorOption {
      return {
        label: value.label,
        value: value.value,
        predefinedShow: filter?.meta.predefined_show,
        predefinedShowText: filter?.meta.predefined_show_text,
        description: filter?.meta.description,
      };
    }

    function getCutoutFilterValueObject(
      filter: ConfigurableProductFilterObject | undefined,
      imageDimensions: ImageDimensions | undefined
    ): ConfigurableProductFilterValueObject | undefined {
      if (!filter || !imageDimensions) {
        return undefined;
      }
      return _.find(
        filter.values,
        (value: ConfigurableProductFilterValueObject) => {
          return (
            Number(value.value) ===
            useImage().getImageCutout(imageDimensions).valueOf()
          );
        }
      );
    }

    function getDefaultFilter(
      filter: ConfigurableProductFilterObject
    ): ConfigurableProductFilterValueObject {
      const firstValue = Object.values(filter.values)[0];
      const defaultValue = filter.values[filter.default] ?? firstValue;
      return useConfiguratorStore().isUploadConfigurator
        ? defaultValue
        : firstValue;
    }

    const filters: Ref<FilterRecord> = ref(
      _.pickBy(
        window.configurable_product!.filters,
        (filter: ConfigurableProductFilterObject) =>
          Object.values(filter.values).length > 0
      )
    );
    const filterOptions: ComputedRef<Record<string, SelectorOption[]>> =
      computed(() =>
        _.mapValues(filters.value, filter =>
          _.map(filter.values, value => filterValueToDropdown(value, filter))
        )
      );
    const selectedFilterOptions: Ref<Record<string, SelectorOption>> = ref(
      _.mapValues(filters.value, filter =>
        filterValueToDropdown(getDefaultFilter(filter))
      )
    );
    const selectedFilterValues: ComputedRef<FilterValueRecord> = computed(() =>
      _.mapValues(
        filters.value,
        (filter, key) => filter.values[selectedFilterOptions.value[key].value]
      )
    );

    const defaultFilterSkus: Ref<Record<string, string>> = ref(
      _.mapValues(filters.value, filter => {
        return filter.default;
      })
    );

    // Get the filter labels/values to display them in the cart
    const filtersForCart: ComputedRef<{ label: string; value: string }[]> =
      computed(() =>
        _.map(filters.value, (filter, key) => ({
          label: filter.meta.name,
          value: selectedFilterOptions.value[key].label,
        }))
      );

    function setFiltersToProductAttributes(
      product: ConfigurableProductSizeValueObject
    ) {
      _.each(selectedFilterValues.value, (_, key) => {
        const selectorOption = filterOptions.value[key]?.find(
          filterValue => filterValue.value === product.attributes[key]
        );
        if (selectorOption) {
          selectedFilterOptions.value[key] = selectorOption;
        }
      });
    }

    function setDefaultFilterSku(
      filterKey: string,
      imageDimensions: ImageDimensions,
      isSettingSelectedFilterOption: boolean = true
    ): void {
      // Get the cutout filter value object
      const cutoutFilterValueObject = getCutoutFilterValueObject(
        filters.value[filterKey],
        imageDimensions
      );

      // If found, set as the selected filter option
      if (cutoutFilterValueObject) {
        if (isSettingSelectedFilterOption) {
          selectedFilterOptions.value[filterKey] = filterValueToDropdown(
            cutoutFilterValueObject,
            filters.value[filterKey]
          );
        }

        defaultFilterSkus.value[filterKey] = cutoutFilterValueObject.value;
      }
    }

    const hasFilters: ComputedRef<boolean> = computed(
      () => !_.isEmpty(filters?.value)
    );
    const hasCutoutFilter: ComputedRef<boolean> = computed(
      () => !!filters?.value[cutoutKey]
    );
    const hasHexagonsFilter: ComputedRef<boolean> = computed(
      () => !!filters?.value[amountHexagonsKey]
    );

    return {
      setFiltersToProductAttributes,
      filterValueToDropdown,
      filters,
      filterOptions,
      selectedFilterOptions,
      selectedFilterValues,
      defaultFilterSkus,
      setDefaultFilterSku,
      filtersForCart,
      hasFilters,
      hasCutoutFilter,
      hasHexagonsFilter,
    };
  },
  { persist: { paths: ['selectedFilterOptions', 'defaultFilterSkus'] } }
);
